import { useState } from "react";
import { waLink } from "@/lib/config";
import { Plus } from "lucide-react";

const FAQS = [
  {
    q: "Do I really not need to create an account?",
    a: "Correct. We deliberately don't require login or accounts. You book by filling a simple form, we send confirmation by email and WhatsApp, and we communicate directly from there.",
  },
  {
    q: "When's the best time to visit Tanzania?",
    a: "For the Great Migration river crossings in northern Serengeti: July–October. For calving season in southern Serengeti: late January–February. The dry season (June–October) is best for general game viewing, when animals gather at the remaining water. Zanzibar is at its best from June to October and December to February.",
  },
  {
    q: "How much driving is involved each day?",
    a: "Between parks you can expect 2-5 hours on the road, broken up by game viewing along the way. Travel times in our itineraries are approximate — road conditions and animal sightings both change the day. Your 4x4 is private, so the pace is yours.",
  },
  {
    q: "What's included in the tour?",
    a: "Airport transfers, a private 4x4 with a pop-up roof and your own English-speaking driver-guide, all park and conservation fees, accommodation as listed, packed lunches on game-drive days and bottled water. International flights, visas, tips, drinks and travel insurance are not included — full details on each tour page.",
  },
  {
    q: "How does booking and payment work?",
    a: "Simply hit Book Now on any tour and send us your dates and group size — no payment is taken online. We'll reply within 24 hours with a tailored itinerary and quote, and once you're happy we'll confirm your dates and share secure payment details.",
  },
  {
    q: "Do you cater for dietary requirements?",
    a: "Absolutely. Vegetarian, vegan, halal, gluten-free, allergies — let us know in the booking form and we'll handle it. Our camp chefs are remarkable.",
  },
  {
    q: "What about safety on safari?",
    a: "All vehicles carry first-aid kits and radios, and your driver-guide is trained in wilderness first aid and knows every park gate and ranger post on the route. You are never handed off to a stranger partway through your trip.",
  },
  {
    q: "Can you customize a trip just for me?",
    a: "Yes — in fact most of our trips are customized. The tours on the site are starting points. Tell us your dates, interests, and budget and we'll build something just for you.",
  },
  {
    q: "What's the visa situation for Tanzania?",
    a: "Most nationalities can get an eVisa online before arrival (about $50 USD) or a visa on arrival at JRO airport. We'll send detailed instructions once you book.",
  },
  {
    q: "Do you offer family or honeymoon packages?",
    a: "Yes to both. Some lodges have family-only sections, dedicated child guides, and shorter game-drive options. For honeymoons we work with romantic, exclusive-use camps. Just mention it in your inquiry.",
  },
];

export default function FAQPage() {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="faq-page" className="pt-36 md:pt-40 pb-24 bg-[#FAF6F0]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center">
          <p className="overline">faqs</p>
          <h1 className="font-display text-5xl md:text-7xl italic font-light mt-4 leading-tight">Frequently Asked Questions</h1>
        </div>

        <div className="mt-16 space-y-3">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className={`bg-[#FFFFFF] rounded-3xl border transition-colors ${isOpen ? "border-[#A6521B]" : "border-[#E2D5C3]"}`}
              >
                <button
                  data-testid={`faq-item-${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="w-full py-6 px-8 flex justify-between items-start gap-6 text-left"
                >
                  <span className="font-marcellus text-xl md:text-2xl text-[#23170E]">{f.q}</span>
                  <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? "bg-[#A6521B] rotate-45 text-white" : "bg-[#A6521B]/10 text-[#A6521B]"}`}>
                    <Plus size={18} />
                  </div>
                </button>
                {isOpen && (
                  <div className="px-8 pb-7 text-[#23170E]/70 leading-relaxed fade-up">{f.a}</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-[#F1E8DC] rounded-3xl p-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl italic font-light">Still wondering about something?</h2>
          <p className="mt-3 text-[#23170E]/65">Ask us directly — we love the curious ones.</p>
          <a href={waLink("Hi APEX, I have a question about your Tanzania tours.")} data-testid="faq-whatsapp-cta" target="_blank" rel="noreferrer" className="inline-flex mt-6 btn-pill bg-[#25D366] text-white hover:bg-[#1ebd5b]">WhatsApp Us</a>
        </div>
      </div>
    </div>
  );
}
